import { useState } from "react";
import Card from "../UI/Card";
import Callout, { type CalloutVariant } from "../UI/Callout";
import Button from "../UI/Button";
import ModalDialog from "../UI/ModalDialog";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import SmtpForm from "./SmtpForm";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import styles from "./Smtp.module.css";

interface SmtpConfig {
  serveur?: string;
  mail?: string;
}

interface SmtpProps {
  customization?: { smtp?: SmtpConfig | null } | null;
  onSave: (data: { smtp?: unknown }) => Promise<unknown>;
  onReset: (fieldsToReset: string[]) => Promise<unknown>;
  isProfile?: boolean;
  onTest: () => Promise<{ data: { success: boolean; error?: string } }>;
}

export default function Smtp({
  customization,
  onSave,
  onReset,
  isProfile,
  onTest,
}: SmtpProps) {
  const [mailResponse, setMailResponse] = useState<{
    type: CalloutVariant;
    content: string;
  } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const smtp = customization?.smtp;

  async function testMail() {
    try {
      const {
        data: { success, error },
      } = await onTest();
      setMailResponse({
        type: success ? "success" : "danger",
        content: success
          ? "Tout semble OK, vous devriez recevoir un mail de test."
          : `Le serveur à renvoyé l'erreur suivante : ${error}.`,
      });
    } catch (error) {
      console.error(error);
      setMailResponse({
        type: "danger",
        content: "Une erreur interne est survenue.",
      });
    }
  }

  const [isTestingMail, testMailLoader] = useLoader(testMail);

  async function saveHandler(values: unknown) {
    await onSave({ smtp: values });
    setModalOpen(false);
  }

  const [resetting, resetHandler] = useLoader(async () => {
    try {
      await onReset(["smtp"]);
    } catch (e) {
      handleAPIError(e);
    }
  });

  const [deleting, deleteHandler] = useLoader(async () => {
    try {
      await onSave({ smtp: null });
    } catch (e) {
      handleAPIError(e);
    }
  });

  return (
    <>
      <ModalDialog
        size="md"
        open={modalOpen}
        onOpenChange={setModalOpen}
        header="Serveur mail"
      >
        <SmtpForm onSubmit={saveHandler} smtp={smtp} />
      </ModalDialog>
      <Card className={styles.card}>
        <div className={styles.header}>
          <Title variant="hecto" weight="medium">
            Serveur mail
          </Title>
          <div className={styles.buttons}>
            <Button
              type="button"
              isLoading={isTestingMail}
              variant="secondary"
              onClick={testMailLoader}
            >
              Tester la connexion
            </Button>
            <Button
              variant="secondary"
              onClick={() => setModalOpen(true)}
              requiresWrite
            >
              Modifier
            </Button>
          </div>
        </div>
        <Text color="muted">
          Envoyez les mails à vos locataires depuis votre propre adresse de
          messagerie
        </Text>

        <Text>
          <Text as="span" weight="medium">
            Serveur SMTP
          </Text>{" "}
          : {smtp?.serveur || "Qalimo (default)"}
        </Text>
        <Text>
          <Text as="span" weight="medium">
            Adresse mail
          </Text>{" "}
          : {smtp?.mail ?? "no-reply@app.qalimo.fr"}
        </Text>
        {mailResponse && (
          <Callout variant={mailResponse.type}>{mailResponse.content}</Callout>
        )}
        <div className={styles.actions}>
          <Button
            type="button"
            variant="tertiary"
            onClick={resetHandler}
            isLoading={resetting}
            requiresWrite
          >
            Utiliser le serveur mail Qalimo
          </Button>
          {isProfile && (
            <Button
              type="button"
              variant="tertiary"
              onClick={deleteHandler}
              isLoading={deleting}
              requiresWrite
            >
              Utiliser le serveur mail du compte
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}
